import { useState, useEffect } from "react";
import { Printer, X, FileText, Filter } from "lucide-react";
import { getOperadorNome } from "@/lib/operador";

/**
 * Modal reutilizável de impressão de relatórios de listas.
 *
 * Abre uma nova janela de impressão com layout A4 otimizado, disparando
 * window.print() — o navegador apresenta o diálogo nativo onde o usuário
 * pode escolher "Salvar como PDF" como destino.
 *
 * Props:
 *  - open: bool
 *  - onOpenChange: (bool) => void
 *  - titulo: título do relatório (ex: "Locais de Acesso")
 *  - subtitulo: string curta (ex: "Interaccess Tecnologia...")
 *  - colunas: Array<{ key, label, align?: "left"|"right"|"center", width?: string, formatter?: (row) => string }>
 *  - allRows: Array<objeto> (todas as linhas sem filtro)
 *  - filteredRows: Array<objeto> (aplicando busca + ordenação)
 *  - sortLabel?: string   (ex: "Empresa ↑")  — opcional, mostrado no cabeçalho
 *  - searchLabel?: string (ex: 'Busca: "ABC"') — opcional
 */
export default function ImprimirListaModal({
  open,
  onOpenChange,
  titulo,
  subtitulo = "",
  colunas,
  allRows = [],
  filteredRows = [],
  sortLabel = "",
  searchLabel = "",
  autoPrint = true,
}) {
  const [escopo, setEscopo] = useState("filtrados");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    // Se não há filtro ativo (todas linhas = filtradas), default para "todos"
    if (allRows.length === filteredRows.length) {
      setEscopo("todos");
    } else {
      setEscopo("filtrados");
    }
  }, [open, allRows.length, filteredRows.length]);

  if (!open) return null;

  const handleImprimir = () => {
    const rows = escopo === "todos" ? allRows : filteredRows;
    const html = buildHTML({
      titulo,
      subtitulo,
      colunas,
      rows,
      sortLabel,
      searchLabel,
      escopo,
      totalAll: allRows.length,
      totalFiltered: filteredRows.length,
      operadorNome: getOperadorNome(),
      autoPrint,
    });
    const win = window.open("", "_blank", "width=1024,height=768");
    if (!win) {
      alert("Por favor, permita janelas popup para imprimir.");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    if (autoPrint) {
      // Aguarda o render e dispara print (onfocus fallback para navegadores que bloqueiam auto-print)
      win.onload = () => {
        setTimeout(() => {
          try {
            win.focus();
            win.print();
          } catch { /* ignore */ }
        }, 300);
      };
    }
    onOpenChange(false);
  };

  const temFiltro = allRows.length !== filteredRows.length;

  return (
    <div
      data-testid="imprimir-modal"
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="glass-card w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="imprimir-modal-close"
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-brand-ice/50 hover:text-brand-ice hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="font-heading font-extrabold text-xl text-brand-ice flex items-center gap-2 text-shadow-logo">
          <Printer className="w-5 h-5 text-brand-light" />
          Imprimir Relatório
        </h2>
        <p className="text-brand-ice/60 text-sm mt-1 font-body">
          {titulo}
        </p>

        <div className="mt-5 space-y-3">
          <label
            data-testid="escopo-todos"
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              escopo === "todos"
                ? "bg-brand-medium/30 border-brand-light/50"
                : "bg-white/5 border-brand-ice/15 hover:bg-white/10"
            }`}
          >
            <input
              type="radio"
              name="escopo"
              value="todos"
              checked={escopo === "todos"}
              onChange={(e) => setEscopo(e.target.value)}
              className="accent-brand-light"
            />
            <FileText className="w-4 h-4 text-brand-ice/70" />
            <div className="flex-1">
              <div className="text-sm font-bold text-brand-ice">
                Todos os registros
              </div>
              <div className="text-xs text-brand-ice/60">
                Imprimir {allRows.length.toLocaleString("pt-BR")} {allRows.length === 1 ? "registro" : "registros"}
              </div>
            </div>
          </label>

          <label
            data-testid="escopo-filtrados"
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              !temFiltro
                ? "opacity-50 cursor-not-allowed bg-white/5 border-brand-ice/10"
                : escopo === "filtrados"
                ? "bg-brand-medium/30 border-brand-light/50"
                : "bg-white/5 border-brand-ice/15 hover:bg-white/10"
            }`}
          >
            <input
              type="radio"
              name="escopo"
              value="filtrados"
              checked={escopo === "filtrados"}
              onChange={(e) => setEscopo(e.target.value)}
              disabled={!temFiltro}
              className="accent-brand-light"
            />
            <Filter className="w-4 h-4 text-brand-ice/70" />
            <div className="flex-1">
              <div className="text-sm font-bold text-brand-ice">
                Apenas filtrados
              </div>
              <div className="text-xs text-brand-ice/60">
                {temFiltro
                  ? `Imprimir ${filteredRows.length.toLocaleString("pt-BR")} ${filteredRows.length === 1 ? "registro" : "registros"} do filtro atual`
                  : "Nenhum filtro aplicado no momento"}
              </div>
            </div>
          </label>
        </div>

        <div className="mt-5 bg-brand-dark/40 border border-brand-ice/10 rounded-lg p-3">
          <p className="text-[11px] text-brand-ice/50 leading-relaxed">
            💡 <strong className="text-brand-ice/70">Dica:</strong> No diálogo do navegador, escolha
            <span className="text-brand-light font-bold"> "Salvar como PDF" </span>
            como destino para gerar o arquivo PDF.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            data-testid="imprimir-cancelar"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-brand-ice/70 hover:text-brand-ice border border-brand-ice/15 text-sm font-heading font-bold transition-colors"
          >
            Cancelar
          </button>
          <button
            data-testid="imprimir-confirmar"
            onClick={handleImprimir}
            className="flex items-center gap-2 bg-brand-medium hover:bg-brand-dark text-white px-5 py-2 rounded-lg font-heading font-bold text-sm shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.97]"
          >
            <Printer className="w-4 h-4" />
            Imprimir / PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Builder de HTML de impressão (A4, tipografia limpa) ----------
function escapeHTML(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildHTML({ titulo, subtitulo, colunas, rows, sortLabel, searchLabel, escopo, totalAll, totalFiltered, operadorNome, autoPrint = true }) {
  const agora = new Date().toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });

  const headerCells = colunas.map((c) => {
    const align = c.align || "left";
    const w = c.width ? `style="width:${c.width}"` : "";
    return `<th class="th-${align}" ${w}>${escapeHTML(c.label)}</th>`;
  }).join("");

  const bodyRows = rows.map((r) => {
    const cells = colunas.map((c) => {
      const val = c.formatter ? c.formatter(r) : r[c.key];
      const align = c.align || "left";
      // rawHtml=true: preserva o HTML do formatter (para badges/spans customizados).
      // Uso restrito a colunas com conteúdo controlado; input do usuário deve continuar
      // escapado pelo formatter antes de compor o HTML.
      const cellContent = c.rawHtml ? (val ?? "") : escapeHTML(val ?? "");
      return `<td class="td-${align}">${cellContent}</td>`;
    }).join("");
    return `<tr>${cells}</tr>`;
  }).join("");

  const infoEscopo = escopo === "todos"
    ? `Todos os ${totalAll.toLocaleString("pt-BR")} registros`
    : `${totalFiltered.toLocaleString("pt-BR")} registro(s) filtrado(s) de ${totalAll.toLocaleString("pt-BR")} total`;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>${escapeHTML(titulo)} - ${agora}</title>
<style>
  @page { size: A4; margin: 12mm 10mm 15mm 10mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
    color: #1a1a1a; margin: 0; padding: 0; font-size: 10pt;
  }
  .header {
    border-bottom: 2px solid #1a365d; padding-bottom: 8px; margin-bottom: 10px;
    display: flex; justify-content: space-between; align-items: flex-end;
  }
  .header-left h1 {
    margin: 0; font-size: 16pt; font-weight: 900; color: #1a365d; letter-spacing: -0.3px;
  }
  .header-left p { margin: 2px 0 0; font-size: 10pt; color: #555; }
  .header-right { text-align: right; font-size: 9pt; color: #666; line-height: 1.4; }
  .header-right strong { color: #1a365d; }
  .infobar {
    display: flex; flex-wrap: wrap; gap: 12px; font-size: 9pt; color: #555;
    margin-bottom: 8px; padding: 6px 0; border-bottom: 1px solid #ddd;
  }
  .infobar span strong { color: #1a365d; }
  table { width: 100%; border-collapse: collapse; font-size: 9pt; }
  thead { display: table-header-group; }
  th {
    background: #1a365d; color: #fff; text-transform: uppercase;
    font-size: 8pt; font-weight: 700; padding: 6px 5px; letter-spacing: 0.4px;
    border-right: 1px solid #2c4a7e;
  }
  th:last-child { border-right: none; }
  td {
    padding: 4px 5px; border-bottom: 1px solid #eee; vertical-align: top;
    white-space: pre-line;
  }
  tr:nth-child(even) td { background: #f7f9fc; }
  .th-left, .td-left { text-align: left; }
  .th-right, .td-right { text-align: right; }
  .th-center, .td-center { text-align: center; }
  .footer {
    position: fixed; bottom: 5mm; left: 10mm; right: 10mm;
    text-align: center; font-size: 8pt; color: #888;
    border-top: 1px solid #ddd; padding-top: 4px;
  }
  .footer .pg::after { content: counter(page) " / " counter(pages); }
  @media print {
    .no-print { display: none !important; }
  }
  .empty {
    text-align: center; padding: 40px; color: #888; font-style: italic;
  }
  .toolbar {
    display: flex; justify-content: flex-end; gap: 8px;
    margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px dashed #ccc;
  }
  .btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px; border-radius: 6px; border: 0;
    font-weight: 700; font-size: 10pt; cursor: pointer;
    transition: background 0.15s;
  }
  .btn-primary { background: #1a365d; color: #fff; }
  .btn-primary:hover { background: #2c4a7e; }
  .btn-secondary { background: #6b7280; color: #fff; }
  .btn-secondary:hover { background: #4b5563; }
  @media print {
    .toolbar { display: none !important; }
  }
</style>
</head>
<body>
  ${!autoPrint ? `
  <div class="toolbar no-print">
    <button class="btn btn-primary" onclick="window.print()">🖨️ Imprimir / Exportar PDF</button>
    <button class="btn btn-secondary" onclick="window.close()">Fechar</button>
  </div>
  ` : ""}
  <div class="header">
    <div class="header-left">
      <h1>${escapeHTML(titulo)}</h1>
      ${subtitulo ? `<p>${escapeHTML(subtitulo)}</p>` : ""}
    </div>
    <div class="header-right">
      <strong>Gerado em:</strong> ${escapeHTML(agora)}<br>
      <strong>Total:</strong> ${rows.length.toLocaleString("pt-BR")} ${rows.length === 1 ? "registro" : "registros"}
    </div>
  </div>

  <div class="infobar">
    <span><strong>Escopo:</strong> ${escapeHTML(infoEscopo)}</span>
    ${operadorNome ? `<span><strong>Operador:</strong> ${escapeHTML(operadorNome)}</span>` : ""}
    ${searchLabel ? `<span><strong>Busca:</strong> ${escapeHTML(searchLabel)}</span>` : ""}
    ${sortLabel ? `<span><strong>Ordenação:</strong> ${escapeHTML(sortLabel)}</span>` : ""}
  </div>

  ${rows.length === 0 ? `
    <div class="empty">Nenhum registro para imprimir</div>
  ` : `
    <table>
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  `}

  <div class="footer">
    SGA Brave Access — Página <span class="pg"></span>
  </div>
</body>
</html>`;
}
